import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';
import { ThemeOverridesToolInputValidationSchema } from '../theme/themeOverrides';
import { CreateFormPageBaseInputValidationSchema } from './create';
import { UpdateFunnelStepInputValidationSchema } from '../page/common/funnel';
import {
  UpdateRedirectUrlPathSegmentValidationSchema,
  UpdateRedirectUrlQueryParameterValidationSchema,
} from '../page/common/redirectAndConfirmation';
import { UpdatePageThemeIdInputValidationSchema } from '../page/common/themeId';
import { UpdatePageFormLayoutInputValidationSchema } from '../page/formLayout';

const formPageUpdateBodyShape = {
  ...CreateFormPageBaseInputValidationSchema.pick({
    name: true,
    title: true,
    slug: true,
    locale: true,
    description: true,
    customizeCheckoutConfirmation: true,
    status: true,
    testmode: true,
    allowDynamicTitle: true,
    allowDynamicDescription: true,
    closePopupOnClickOutside: true,
    hostedLayoutType: true,
    themeId: true,
    themeOverrides: true,
    redirect: true,
    afterPaymentAction: true,
    confirmationCheckoutTitle: true,
    confirmationCheckoutMessage: true,
    redirectUrl: true,
    redirectUrlInsideEmbed: true,
    redirectPageId: true,
    sendPaymentNotification: true,
    notifyEmail: true,
    sendEmailConfirmation: true,
    customizeEmailConfirmation: true,
    confirmationEmailSubject: true,
    confirmationEmailMessage: true,
    confirmationEmailShowLogo: true,
    confirmationEmailShowStoreName: true,
    googleIndex: true,
    trackingCodes: true,
    invoiceSettings: true,
    imageIds: true,
    fileIds: true,
    formDetails: true,
    funnelSteps: true,
    redirectUrlPath: true,
    redirectUrlQuery: true,
  }).partial().extend({
    themeId: UpdatePageThemeIdInputValidationSchema,
    formLayout: UpdatePageFormLayoutInputValidationSchema.optional(),
    // Update can't create fields to key against (fields is excluded from
    // this body), so field references inside funnel steps and redirect
    // path/query segments must resolve by {fieldId}, not the create-time
    // {fieldKey} — mirrors apps/api/src/pages/services/updateFormPage.ts.
    funnelSteps: z.array(UpdateFunnelStepInputValidationSchema).nullish().meta({
      description:
        'Ordered funnel configuration for this page. Supports intermediate checkout steps, one-click upsells, and confirmation behavior. Field references inside upsell/confirmation redirect steps resolve by `fieldId` (get current field IDs from the page detail response) — the create-time `fieldKey` string is rejected.',
    }),
    redirectUrlPath: z.array(UpdateRedirectUrlPathSegmentValidationSchema).nullish().meta({
      description:
        'Additional path parameters appended to the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` (the persisted field ID, from the page detail response) instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [
        { key: 'fields', fieldId: '507f1f77bcf86cd799439011' },
        { key: 'orderId', identifier: 'orderId' },
      ],
    }),
    redirectUrlQuery: z.array(UpdateRedirectUrlQueryParameterValidationSchema).nullish().meta({
      description:
        'Query parameters for the `redirectUrl`. Applies if `afterPaymentAction` is `redirect` and `redirectUrl` is set. To reference a field value, use `fieldId` instead of the create-time `fieldKey`. [Learn more](https://checkoutpage.com/docs/articles/add-field-and-variant-data-to-redirect-url)',
      example: [{ parameter: 'name', key: 'fields', fieldId: '507f1f77bcf86cd799439011' }],
    }),
  }).shape,
};

/**
 * The canonical list of fields a merchant may PATCH on a form page. Derived
 * from `formPageUpdateBodyShape` so the API controller and the MCP tool can
 * iterate the same list without re-declaring it (drift-proof — see
 * `apps/api/src/pages/schemas/formPageUpdateFields.driftGuard.test.ts`).
 * `fields` is intentionally excluded — custom form fields are managed via
 * their own endpoint, not this PATCH body.
 */
export const FORM_PAGE_UPDATE_FIELDS = Object.keys(
  formPageUpdateBodyShape,
) as ReadonlyArray<keyof typeof formPageUpdateBodyShape>;

/**
 * Unrefined, non-strict base for the update body. The API's PATCH endpoint
 * builds on this (it stays lenient and re-adds the email-confirmation
 * pairing refine); the MCP body below layers strict() and the refines on top.
 */
export const FormPageUpdateBodyBaseValidationSchema = z.object(formPageUpdateBodyShape);

// customizeEmailConfirmation and the subject/message pair must be set
// together or not at all. Shared with the API's update schema.
export const hasValidCustomEmailConfirmationPairing = (data: {
  customizeEmailConfirmation?: boolean | null;
  confirmationEmailSubject?: string | null;
  confirmationEmailMessage?: string | null;
}) => {
  const customizeEmailConfirmation = data.customizeEmailConfirmation === true;
  const hasSubject = !!data.confirmationEmailSubject;
  const hasMessage = !!data.confirmationEmailMessage;
  if (customizeEmailConfirmation) return hasSubject && hasMessage;
  return !hasSubject && !hasMessage;
};

export const CUSTOM_EMAIL_CONFIRMATION_REFINE_OPTIONS = {
  message:
    'Must pass `confirmationEmailSubject` and `confirmationEmailMessage` when `customizeEmailConfirmation`.',
  path: ['customizeEmailConfirmation'],
};

export const FormPageUpdateBodyValidationSchema = z
  .object(formPageUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  })
  .refine(hasValidCustomEmailConfirmationPairing, CUSTOM_EMAIL_CONFIRMATION_REFINE_OPTIONS);

// MCP update tool input; themeOverrides is loose, see ThemeOverridesToolInputValidationSchema.
export const UpdateFormPageInputValidationSchema = z
  .object({
    pageId: ObjectIdStringValidationSchema.meta({
      description: 'The ID of the form page to update.',
    }),
    ...formPageUpdateBodyShape,
    themeOverrides: ThemeOverridesToolInputValidationSchema,
  })
  .refine(
    value => {
      const { pageId: _pageId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one update field must be provided.' },
  )
  .refine(hasValidCustomEmailConfirmationPairing, CUSTOM_EMAIL_CONFIRMATION_REFINE_OPTIONS);

export type UpdateFormPageInput = z.infer<typeof UpdateFormPageInputValidationSchema>;
